import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ConnectionState, socket } from '../App';
import { CheckCircle } from 'lucide-react';

interface LoginProps {
    connectionState: ConnectionState;
}

export function Login({ connectionState }: LoginProps) {
    const [usePairing, setUsePairing] = useState(false);
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resetting, setResetting] = useState(false);

    const pairingCode = connectionState.pairingCode;

    const resetSession = async () => {
        setResetting(true);
        setError('');
        try {
            await fetch('/api/wa/reset', { method: 'POST', credentials: 'include' });
            setPhone('');
        } catch {
            setError('Erreur reset');
        }
        setTimeout(() => setResetting(false), 3000);
    };

    const requestCode = () => {
        if (!phone || phone.replace(/\D/g, '').length < 8) {
            setError('Numéro invalide (ex : 33612345678)');
            return;
        }
        setError('');
        setLoading(true);
        if (socket) socket.emit('request-pairing-code', { phone });
        setTimeout(() => { setLoading(false); setError('Pas de réponse — réessaie'); }, 20_000);
    };

    return (
        <div className="grid-2col">

            {/* WhatsApp */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                    <span style={{ fontSize: 24 }}>💬</span>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>WhatsApp</h2>
                    {connectionState.whatsapp && <CheckCircle color="#22c55e" size={22} />}
                </div>

                {connectionState.whatsapp ? (
                    <div style={{ width: 220, height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4', borderRadius: 12, gap: 12 }}>
                        <CheckCircle color="#22c55e" size={60} />
                        <span style={{ color: '#15803d', fontWeight: 700, fontSize: 16 }}>Connecté ✓</span>
                    </div>

                ) : pairingCode ? (
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 16px' }}>Saisis ce code dans WhatsApp :</p>
                        <div style={{ fontSize: 38, fontWeight: 900, letterSpacing: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'monospace', padding: '16px 24px', border: '2px dashed #c4b5fd', borderRadius: 12, margin: '0 0 16px' }}>
                            {pairingCode}
                        </div>
                        <ol style={{ color: '#6b7280', fontSize: 12, textAlign: 'left', lineHeight: 1.8, paddingLeft: 18, margin: '0 0 16px' }}>
                            <li>Ouvre WhatsApp sur ton téléphone</li>
                            <li>Paramètres → Appareils liés</li>
                            <li>Lier avec numéro de téléphone</li>
                            <li>Saisis le code ci-dessus</li>
                        </ol>
                        <button onClick={() => { setPhone(''); setUsePairing(false); }}
                            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 12, cursor: 'pointer', color: '#6b7280' }}>
                            ↺ Recommencer
                        </button>
                    </div>

                ) : usePairing ? (
                    /* Mode code numérique */
                    <div style={{ width: '100%', textAlign: 'center' }}>
                        <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 16px' }}>
                            Entre ton numéro pour recevoir un code de jumelage.
                        </p>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            <input
                                type="tel"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && requestCode()}
                                placeholder="33612345678"
                                style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15, outline: 'none', fontFamily: 'monospace' }}
                            />
                            <button onClick={requestCode} disabled={loading}
                                style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: loading ? '#c4b5fd' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'wait' : 'pointer' }}>
                                {loading ? '…' : 'Code'}
                            </button>
                        </div>
                        {error && <p style={{ color: '#ef4444', fontSize: 12, margin: '0 0 8px' }}>{error}</p>}
                        <button onClick={resetSession} disabled={resetting}
                            style={{ marginBottom: 8, padding: '5px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#ef4444', fontSize: 11, cursor: 'pointer', display: 'block', width: '100%' }}>
                            {resetting ? '⏳ Réinitialisation…' : '🔄 Réinitialiser la session'}
                        </button>
                        <button onClick={() => { setUsePairing(false); setError(''); }}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                            ← Scanner le QR à la place
                        </button>
                    </div>

                ) : (
                    /* Mode QR — par défaut */
                    <div style={{ textAlign: 'center' }}>
                        {connectionState.whatsappQr ? (
                            <>
                                <QRCodeSVG value={connectionState.whatsappQr} size={200} />
                                <p style={{ color: '#6b7280', fontSize: 12, marginTop: 12, lineHeight: 1.6 }}>
                                    WhatsApp → Paramètres → Appareils liés<br />→ Lier un appareil → scanner ce QR
                                </p>
                            </>
                        ) : (
                            <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8, fontSize: 13 }}>
                                Génération du QR…
                            </div>
                        )}
                        <button onClick={() => { setUsePairing(true); setError(''); }}
                            style={{ marginTop: 14, background: 'none', border: 'none', color: '#9ca3af', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                            Utiliser un code à la place →
                        </button>
                    </div>
                )}
            </div>

            {/* Signal */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                    <span style={{ fontSize: 24 }}>🔵</span>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Signal</h2>
                    {connectionState.signal && <CheckCircle color="#3b82f6" size={22} />}
                </div>

                {connectionState.signal ? (
                    <div style={{ width: 220, height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#eff6ff', borderRadius: 12, gap: 12 }}>
                        <CheckCircle color="#3b82f6" size={60} />
                        <span style={{ color: '#1d4ed8', fontWeight: 700, fontSize: 16 }}>Connecté ✓</span>
                        <span style={{ color: '#3b82f6', fontSize: 13 }}>{connectionState.signalNumber}</span>
                    </div>
                ) : connectionState.signalApiAvailable ? (
                    <div style={{ textAlign: 'center' }}>
                        {connectionState.signalQrImage ? (
                            <img src={connectionState.signalQrImage} alt="Signal QR" width={200} height={200} style={{ borderRadius: 8 }} />
                        ) : (
                            <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8 }}>
                                Génération du QR…
                            </div>
                        )}
                        <p style={{ color: '#6b7280', fontSize: 12, marginTop: 12 }}>
                            Signal → Paramètres → Appareils liés → Lier un appareil
                        </p>
                    </div>
                ) : (
                    <div style={{ width: 200, height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                        <span style={{ fontSize: 32, marginBottom: 8 }}>🔵</span>
                        <p style={{ margin: 0, fontSize: 12 }}>API Signal non disponible</p>
                    </div>
                )}
            </div>
        </div>
    );
}
